import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { RECIPIENT_TYPES } from 'components/common/constants';
import {
  CYRILLIC_REGEXP,
  getCustomerNameRegExp
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import ValidateRecipientDetailsModel from 'components/NewPaymentFormV2/models/ValidateRecipientDetailsModel';
import i18nContext from 'components/i18n-context';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import Button from 'uikit/Button/Button';
import './RecipientDetailsStep.scss';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';

const RecipientDetailsStep = ({ newPaymentStore, customerNumber }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      recipientType: newPaymentStore.recipientDetailsStep?.recipientType || RECIPIENT_TYPES.COMPANY,
      companyName: newPaymentStore.recipientDetailsStep?.companyName || '',
      firstName: newPaymentStore.recipientDetailsStep?.firstName || '',
      lastName: newPaymentStore.recipientDetailsStep?.lastName || ''
    },
    validationSchema: Yup.object().shape({
      companyName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.COMPANY,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(70, i18n.getMessage('client.validation.message.maxSize', { size: 70 }))
            .matches(getCustomerNameRegExp(), {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
              })
            }),
        otherwise: (schema) => schema
      }),
      firstName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.INDIVIDUAL,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
            .matches(getCustomerNameRegExp(), {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
              })
            }),
        otherwise: (schema) => schema
      }),
      lastName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.INDIVIDUAL,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
            .matches(getCustomerNameRegExp(), {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
              })
            }),
        otherwise: (schema) => schema
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidateRecipientDetailsModel({
        ...newPaymentStore.newPaymentFormData,
        ...newPaymentStore.recipientDetailsStep,
        ...values
      });

      newPaymentStore.updateRecipientDetailsStep(values);

      await newPaymentStore.validateRecipientDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue } = form;

  const handleRecipientTypeChange = (name, value) => {
    setFieldValue('recipientType', value);

    if (value === RECIPIENT_TYPES.COMPANY) {
      setFieldValue('firstName', '');
      setFieldValue('lastName', '');
    } else {
      setFieldValue('companyName', '');
    }
  };

  const serverError = newPaymentStore.error;

  return (
    <form onSubmit={handleSubmit} className={'new-payment-recipient-details-step'}>
      <div className={'new-payment-recipient-details-step-inputs-wrapper'}>
        <InputDropDown
          className={'new-payment-recipient-details-step-input'}
          label={i18n.getMessage('templates.form.recipientType.label')}
          name={'recipientType'}
          value={form.values.recipientType}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
          isRequired
          error={errors.recipientType || getNewPaymentFormStepErrorMessage(i18n, serverError, 'recipientType')}
        />

        {values.recipientType === RECIPIENT_TYPES.COMPANY ? (
          <Input
            className={'new-payment-recipient-details-step-input'}
            name={'companyName'}
            label={i18n.getMessage('templates.form.companyName.label')}
            placeholder={i18n.getMessage('templates.form.companyName.placeholder')}
            value={values.companyName}
            error={errors.companyName || getNewPaymentFormStepErrorMessage(i18n, serverError, 'companyName')}
            onChange={handleChange}
            isRequired
          />
        ) : (
          <>
            <Input
              className={'new-payment-recipient-details-step-input'}
              name={'firstName'}
              label={i18n.getMessage('templates.form.firstName.label')}
              placeholder={i18n.getMessage('templates.form.firstName.placeholder')}
              value={values.firstName}
              error={errors.firstName || getNewPaymentFormStepErrorMessage(i18n, serverError, 'firstName')}
              onChange={handleChange}
              isRequired
            />
            <Input
              className={'new-payment-recipient-details-step-input'}
              name={'lastName'}
              label={i18n.getMessage('templates.form.lastName.label')}
              placeholder={i18n.getMessage('templates.form.lastName.placeholder')}
              value={values.lastName}
              error={errors.lastName || getNewPaymentFormStepErrorMessage(i18n, serverError, 'lastName')}
              onChange={handleChange}
              isRequired
            />
          </>
        )}
      </div>

      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-recipient-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

RecipientDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(RecipientDetailsStep));
